import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopHeaderCatalogue.styles';
import { useDesktopHeaderCategories } from './DesktopHeaderCatalogue.hooks';
import { DesktopHeaderCategories } from './DesktopHeaderCategories';
import { useDesktopCatalogueStore } from '@/store/useDesktopCatalogueStore';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { Portal } from '@/components/UI/Portal/Portal';

const DesktopHeaderCatalogueContent = () => {
  const [isDesktopCatalogueOpen] = useDesktopCatalogueStore(state => [
    state.isDesktopCatalogueOpen,
  ]);
  const {
    items,
    loading,
    selectedItemId,
    setSelectedItemId,
    subItems,
    selectedSubItemId,
    setSelectedSubItemId,
    primaryPropertyOptions,
    selectedPrimaryPropertyOptionId,
    setSelectedPrimaryPropertyOptionId,
  } = useDesktopHeaderCategories(isDesktopCatalogueOpen);
  const [isColumnsShown, setIsColumnsShown] = useState(true);

  const secondColumnShown = isColumnsShown && !!subItems.length;
  const thirdColumnShown = isColumnsShown && !!primaryPropertyOptions.length;

  function onMouseLeave() {
    setIsColumnsShown(false);
    setSelectedItemId(null);
    setSelectedSubItemId(null);
    setSelectedPrimaryPropertyOptionId(null);
  }

  function onSecondColumnLeave(event: React.MouseEvent<HTMLDivElement>) {
    const { relatedTarget, currentTarget } = event;

    if (
      relatedTarget &&
      currentTarget.previousElementSibling?.contains(
        relatedTarget as Node | null
      )
    ) {
      setSelectedSubItemId(null);
    }
  }

  function onThirdColumnLeave() {
    setSelectedPrimaryPropertyOptionId(null);
  }

  return (
    <S.DesktopHeaderCatalogueWrapper>
      <S.DesktopHeaderCatalogueBox>
        <S.DesktopHeaderCatalogueContent
          data-testid={CommonTestIds.desktopHeaderCatalogue.content}
          onMouseEnter={() => setIsColumnsShown(true)}
          onMouseLeave={onMouseLeave}
        >
          <DesktopHeaderCategories
            items={items}
            selectedItemId={selectedItemId}
            onSelect={setSelectedItemId}
            isFirstColumn
            loading={loading}
          />
          {secondColumnShown && (
            <DesktopHeaderCategories
              items={subItems}
              selectedItemId={selectedSubItemId}
              onSelect={setSelectedSubItemId}
              onColumnLeave={onSecondColumnLeave}
            />
          )}
          {thirdColumnShown && (
            <DesktopHeaderCategories
              items={primaryPropertyOptions}
              selectedItemId={selectedPrimaryPropertyOptionId}
              onSelect={setSelectedPrimaryPropertyOptionId}
              onColumnLeave={onThirdColumnLeave}
            />
          )}
        </S.DesktopHeaderCatalogueContent>
      </S.DesktopHeaderCatalogueBox>
    </S.DesktopHeaderCatalogueWrapper>
  );
};

export const DesktopHeaderCatalogue = () => {
  const { t } = useTranslation();
  const [isDesktopCatalogueOpen, toggleDesktopCatalogue] =
    useDesktopCatalogueStore(state => [
      state.isDesktopCatalogueOpen,
      state.toggleDesktopCatalogue,
    ]);
  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);

  function onClick() {
    toggleDesktopCatalogue();

    if (isNavOpen) toggleNav();
  }

  return (
    <>
      <S.DesktopHeaderCatalogueBtn
        data-testid={CommonTestIds.desktopHeaderCatalogue.button}
        $isOpen={isDesktopCatalogueOpen}
        onClick={onClick}
      >
        <S.DesktopHeaderCatalogueBtnIcon $isOpen={isDesktopCatalogueOpen} />
        <span>{t('catalogue.catalogue')}</span>
      </S.DesktopHeaderCatalogueBtn>
      <Portal>
        <S.DesktopHeaderCatalogueOverlay
          data-testid={CommonTestIds.desktopHeaderCatalogue.overlay}
          isOpen={isDesktopCatalogueOpen}
          onClick={toggleDesktopCatalogue}
        />
      </Portal>
      {isDesktopCatalogueOpen && <DesktopHeaderCatalogueContent />}
    </>
  );
};
